import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  CollectionParameterCreateInput,
  CollectionParameterDataTypes,
  CollectionParameterUpdateInput,
  GetCollectionParametersQuery,
  useCreateCollectionParameterMutation,
  useDeleteCollectionParameterMutation,
  useGetCollectionParametersQuery,
  useUpdateCollectionParameterMutation,
} from 'data/generated/graphql';

export type CollectionParameterRow = {
  key: string;
  label: string;
  description?: string;
  enabled: boolean;
  deprecated: boolean;
  dataType: CollectionParameterDataTypes;
  /** Only present when dataType === 'enum'. Lists selectable options for the parameter. */
  value?: string[];
  createdAt: number;
  updatedAt: number;
};

type ServerParameter = GetCollectionParametersQuery['getCollectionParameters'][number];

export const useCollectionParameters = (publicationKey: string) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useGetCollectionParametersQuery(
    { publicationKey },
    { enabled: !!publicationKey }
  );

  const parameters: CollectionParameterRow[] = (data?.getCollectionParameters ?? []).map((p) => ({
    key: p.key,
    label: p.label,
    description: p.description ?? undefined,
    enabled: p.enabled,
    deprecated: p.deprecated,
    dataType: p.dataType,
    value: p.value ?? undefined,
    createdAt: p.createdAt,
    updatedAt: p.updatedAt,
  }));

  // Patch the cached GetCollectionParameters query so the UI reflects the mutation payload
  // directly rather than waiting on a refetch that can race with backend eventual consistency.
  const patchCachedParameters = useCallback(
    (reducer: (list: ServerParameter[]) => ServerParameter[]) => {
      queryClient.setQueryData<GetCollectionParametersQuery | undefined>(
        ['GetCollectionParameters', { publicationKey }],
        (prev) =>
          prev
            ? {
                ...prev,
                getCollectionParameters: reducer(prev.getCollectionParameters),
              }
            : prev
      );
    },
    [queryClient, publicationKey]
  );

  const createMutation = useCreateCollectionParameterMutation();
  const updateMutation = useUpdateCollectionParameterMutation();
  const deleteMutation = useDeleteCollectionParameterMutation();

  const createParameter = useCallback(
    (input: Omit<CollectionParameterCreateInput, 'publicationKey'>) =>
      new Promise<void>((resolve, reject) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        const label = input.label.trim() ?? '';
        if (!label) {
          reject(new Error('Label is required and cannot be empty.'));
          return;
        }
        createMutation.mutate(
          { input: { ...input, label, publicationKey } },
          {
            onSuccess: ({ createCollectionParameter }) => {
              patchCachedParameters((list) => [...list, createCollectionParameter]);
              resolve();
            },
            onError: reject,
          }
        );
      }),
    [createMutation, publicationKey, patchCachedParameters]
  );

  const updateParameter = useCallback(
    (input: Omit<CollectionParameterUpdateInput, 'publicationKey'>) =>
      new Promise<void>((resolve, reject) => {
        const label = input.label?.trim() ?? '';
        if (!label) {
          reject(new Error('Label is required and cannot be empty.'));
          return;
        }
        updateMutation.mutate(
          { input: { ...input, label, publicationKey } },
          {
            onSuccess: ({ updateCollectionParameter }) => {
              patchCachedParameters((list) =>
                list.map((p) => (p.key === updateCollectionParameter.key ? updateCollectionParameter : p))
              );
              resolve();
            },
            onError: reject,
          }
        );
      }),
    [updateMutation, publicationKey, patchCachedParameters]
  );

  const deleteParameter = useCallback(
    (key: string) =>
      new Promise<void>((resolve, reject) => {
        deleteMutation.mutate(
          { input: { publicationKey, key } },
          {
            onSuccess: () => {
              patchCachedParameters((list) => list.filter((p) => p.key !== key));
              resolve();
            },
            onError: reject,
          }
        );
      }),
    [deleteMutation, publicationKey, patchCachedParameters]
  );

  return {
    parameters,
    isLoading,
    error,
    createParameter,
    updateParameter,
    deleteParameter,
  };
};
